import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Send, Search, MessageCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { listContactsForBroadcast, listTemplates, sendWhatsappBroadcast } from "@/lib/api/whatsapp.functions";

export const Route = createFileRoute("/_authenticated/m/envio-mensagens")({
  component: Page,
});

function Page() {
  const _list = useServerFn(listContactsForBroadcast);
  const _listT = useServerFn(listTemplates);
  const _send = useServerFn(sendWhatsappBroadcast);

  const [filters, setFilters] = useState({ responsavel: "", aluno: "", anoNasc: "" });
  const [rows, setRows] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [templateId, setTemplateId] = useState<string>("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [contacts, tpls] = await Promise.all([
        _list({ data: { ...filters, anoNasc: filters.anoNasc || undefined } }),
        _listT(),
      ]);
      setRows(contacts as any[]);
      setTemplates(tpls as any[]);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-line */ }, []);

  const allYears = useMemo(() => {
    const ys = new Set<number>();
    rows.forEach((r) => r.anoNasc && ys.add(r.anoNasc));
    return Array.from(ys).sort();
  }, [rows]);

  const toggle = (id: string) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const onTemplate = (id: string) => {
    setTemplateId(id);
    if (id === "__none__") { setMessage(""); return; }
    const t = templates.find((x) => x.id === id);
    if (t) setMessage(t.message);
  };

  const send = async () => {
    if (!message.trim()) return toast.error("Digite uma mensagem.");
    const recipients: Array<{ name: string; phone?: string }> = rows
      .filter((r) => selected.has(r.id))
      .map((r) => ({ name: r.aluno, phone: r.whatsapp }));
    if (recipients.length === 0) return toast.error("Selecione ao menos um destinatário.");
    setSending(true);
    try {
      const res = (await _send({
        data: { message, templateId: templateId && templateId !== "__none__" ? templateId : null, recipients },
      })) as any;
      toast.success(`Enviadas: ${res.sent} • Falhas: ${res.failed}`);
      if (res.errors?.length) console.warn(res.errors);
      setSelected(new Set());
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
        <MessageCircle className="w-6 h-6 text-green-600" /> Envio de Mensagens
      </h1>
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>Selecione os Destinatários</CardTitle>
            <CardDescription>Filtre e selecione os contatos que receberão a mensagem.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Responsável</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input className="pl-7 h-9" placeholder="Buscar..." value={filters.responsavel}
                    onChange={(e) => setFilters({ ...filters, responsavel: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Aluno</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input className="pl-7 h-9" placeholder="Buscar..." value={filters.aluno}
                    onChange={(e) => setFilters({ ...filters, aluno: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ano de Nasc.</Label>
                <Select value={filters.anoNasc || "__all__"} onValueChange={(v) => setFilters({ ...filters, anoNasc: v === "__all__" ? "" : v })}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {allYears.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="outline" className="h-9 w-full" onClick={refresh} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aplicar"}
                </Button>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr className="text-left">
                      <th className="p-2 w-10">
                        <Checkbox checked={rows.length > 0 && selected.size === rows.length}
                          onCheckedChange={toggleAll} />
                      </th>
                      <th className="p-2">Aluno</th>
                      <th className="p-2">Ano Nasc.</th>
                      <th className="p-2">Responsável</th>
                      <th className="p-2">WhatsApp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={5} className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Nenhum contato encontrado.</td></tr>
                    ) : rows.map((r) => (
                      <tr key={r.id} className="border-t hover:bg-muted/30">
                        <td className="p-2"><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} /></td>
                        <td className="p-2 font-medium">{r.aluno}</td>
                        <td className="p-2">{r.anoNasc ?? "—"}</td>
                        <td className="p-2">{r.responsavel || <span className="text-muted-foreground">—</span>}</td>
                        <td className="p-2">{r.whatsapp || <Badge variant="outline">Sem nº</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-right">{selected.size} selecionado(s) de {rows.length}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Mensagem</CardTitle>
            <CardDescription>Escreva ou escolha um template.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Template Salvo</Label>
              <Select value={templateId || "__none__"} onValueChange={onTemplate}>
                <SelectTrigger><SelectValue placeholder="Nenhum (Mensagem Livre)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nenhum (Mensagem Livre)</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Conteúdo da Mensagem</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Olá {nome}, sua mensagem aqui..." className="min-h-[180px]" />
              <p className="text-xs text-muted-foreground">A variável <code className="bg-muted px-1 rounded">{`{nome}`}</code> será substituída pelo nome do contato.</p>
            </div>
            <Button className="w-full" onClick={send} disabled={sending || selected.size === 0}>
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar para {selected.size} destinatário(s)
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
